import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import db from "@/db";

import { contributorsConfig } from "@/setup/openApiConfig";
import { ProjectContributorApi } from "@socotec.io/contributors-sdk";
import { buildQueryset } from "@/utils/queryset";
import { useProjectsStore } from "@/store/modules/projects";
import { useUsersStore } from "@/store/modules/users";

const projectContributorApiClient = new ProjectContributorApi(
  contributorsConfig
);

const Contributor = db.models.Contributor;
const CompanyProjectRole = db.models.CompanyProjectRole;

let pendingContributorsFetch = null;

export const useContributorsStore = defineStore("contributors", {
  state: () => ({
    contributorIsLoading: false,
    contributors: [],
    isCurrentUserCanChange: false,
  }),
  actions: {
    async setUserCanChange(projectUuid) {
      const usersStore = useUsersStore();
      const currentUserEmail = usersStore.currentUserEmail;
      const isCurrentUserProjectManagerOrDeveloper =
        usersStore.isCurrentUserProjectManagerOrDeveloper;
      await this.fetchContributors(projectUuid);

      const companyProjectRoleIds = await db.companyProjectRoles
        .where({ projectId: projectUuid })
        .primaryKeys();
      const projectContributors = await db.contributors
        .where("companyProjectRoleId")
        .anyOf(companyProjectRoleIds)
        .toArray();

      const contributorEmails = projectContributors.map(
        ({ userEmail }) => userEmail
      );

      this.contributors = projectContributors;
      this.isCurrentUserCanChange =
        contributorEmails.includes(currentUserEmail) ||
        isCurrentUserProjectManagerOrDeveloper;
    },

    async fetchContributors(projectUuid) {
      if (isNetworkUnavailable() || useProjectsStore().isOfflineState) return;
      if (pendingContributorsFetch?.projectUuid === projectUuid) {
        return pendingContributorsFetch.promise;
      }
      const promise = this.loadContributors(projectUuid);
      pendingContributorsFetch = { projectUuid, promise };
      try {
        return await promise;
      } finally {
        if (pendingContributorsFetch?.promise === promise) {
          pendingContributorsFetch = null;
        }
      }
    },

    async loadContributors(projectUuid) {
      this.contributorIsLoading = true;
      try {
        const { results } = await projectContributorApiClient.list({
          projectUuid,
          isInternal: true,
        });
        for (const contributor of results) {
          await db.models.Contributor.createOrUpdate(contributor);
          await db.models.CompanyProjectRole.createOrUpdate(
            contributor.companyProjectRole
          );
        }
        this.contributors = await db.contributors
          .where("companyProjectRoleId")
          .anyOf(
            await db.companyProjectRoles
              .where({ projectId: projectUuid })
              .primaryKeys()
          )
          .toArray();
      } finally {
        this.contributorIsLoading = false;
      }
    },

    async filterContributors({
      queryAsObject = {},
      ordering = "",
      filterMapping = {},
    } = {}) {
      if (queryAsObject?.roleId) {
        const companyProjectRoleIds = await db.companyProjectRoles
          .where({ roleId: queryAsObject.roleId })
          .primaryKeys();
        delete queryAsObject.roleId;

        queryAsObject.companyProjectRoleId = companyProjectRoleIds;
      }

      if (queryAsObject?.projectId) {
        const companyProjectRoleIds = await db.companyProjectRoles
          .where({ projectId: queryAsObject.projectId })
          .primaryKeys();
        delete queryAsObject.projectId;

        if (queryAsObject.companyProjectRoleId)
          queryAsObject.companyProjectRoleId =
            queryAsObject.companyProjectRoleId.filter((e) =>
              companyProjectRoleIds.includes(e)
            );
        else {
          queryAsObject.companyProjectRoleId = companyProjectRoleIds;
        }
      }

      while (this.contributorIsLoading)
        await new Promise((r) => setTimeout(r, 200));

      this.contributorIsLoading = true;
      try {
        const { queryset } = await buildQueryset(
          db.contributors,
          { ...queryAsObject, page_size: 100 },
          ordering,
          filterMapping
        );

        this.contributors = await queryset.toArray();
      } finally {
        this.contributorIsLoading = false;
      }
    },

    async createContributor({ projectUuid, form }) {
      const contributorReponse = await projectContributorApiClient.create({
        projectUuid,
        contributorInputRequest: form,
      });
      const dexieUuid = await Contributor.createOrUpdate({
        ...contributorReponse,
      });
      await CompanyProjectRole.createOrUpdate({
        ...contributorReponse.companyProjectRole,
      });

      const newContributor = await db.contributors.get({ uuid: dexieUuid });

      this.contributors.push(newContributor);
      return newContributor;
    },

    async updateContributor({ projectUuid, form }) {
      const { uuid } = form;
      const contributorResponse =
        await projectContributorApiClient.partialUpdate({
          uuid,
          projectUuid,
          patchedContributorInputRequest: form,
        });
      const success = await db.contributors.update(uuid, contributorResponse);

      if (!success) console.warn(`Could not update contributor ${uuid}`);

      await CompanyProjectRole.createOrUpdate({
        ...contributorResponse.companyProjectRole,
      });

      const contributor = await db.contributors.get({ uuid });
      await contributor.setRelationships();

      const index = this.contributors.findIndex(
        (item) => contributor.uuid === item.uuid
      );
      if (index >= 0) {
        this.contributors[index] = contributor;
        this.contributors = [...this.contributors];
      }

      return contributor;
    },

    async destroyContributor({ projectUuid, uuid }) {
      await projectContributorApiClient.destroy({ uuid, projectUuid });
      await db.contributors.delete(uuid);

      this.contributors = this.contributors.filter(
        (contributor) => contributor.uuid !== uuid
      );
    },
  },
});
